<template>
  <div class="overflow-auto rounded-lg shadow">
    <table class="w-full border-collapse" border="0" cellpadding="0" cellspacing="0">
      <thead class="bg-ontick-steel-600 text-left">
        <tr>
          <th v-for="header in headers" :key="header.label">
            <div class="header-cell" :class="{ '!text-center': header.label == 'Tendência' }">
              <span
                :class="[{ hidden: header.hidden }, { 'text-center': header.label == 'Tendência' }]"
                class="whitespace-wrap"
              >
                {{ header.label }}
              </span>
            </div>
          </th>
        </tr>
      </thead>
      <transition-group tag="tbody" name="fade" mode="out-in" class="bg-ontick-graphite-500">
        <tr v-if="is_loading" key="loading">
          <td colspan="1000">
            <div class="bg-ontick-graphite-500 flex w-full items-center justify-center py-3">
              <i v-html="$icons.loading"> </i>
              <span class="sr-only"> Carregando </span>
            </div>
          </td>
        </tr>
        <tr v-else-if="bodyData.length == 0" key="no_data">
          <td colspan="1000">
            <div class="bg-ontick-graphite-500 flex w-full items-center justify-center py-3">
              <span> Nenhum best encontrado </span>
            </div>
          </td>
        </tr>
        <template v-else>
          <component
            :is="tableToShow"
            v-for="(best, index) in bodyData"
            :selected_tab="selected_tab"
            :key="best.best_id + selected_tab + index"
            :data="best"
          />
        </template>
      </transition-group>
    </table>
  </div>
</template>

<script>
import bestAvailableTableRowComponent from "./best-available-table-row.component.vue";
import bestRunningTableRowComponent from "./best-running-table-row.component.vue";
import bestHistoryTableRowComponent from "./best-history-table-row.component.vue";
import { mapActions, mapGetters } from "vuex";
import { v4 as uuid } from "uuid";

export default {
  components: {
    bestAvailableTableRowComponent,
    bestRunningTableRowComponent,
    bestHistoryTableRowComponent,
  },
  props: {
    available: {
      type: Array,
    },
    running: {
      type: Array,
    },
    history: {
      type: Array,
    },
    selected_tab: {
      type: String,
    },
    is_loading: {
      type: Boolean,
    },
  },

  data() {
    return {
      connectionStatus: {
        awaitingConnection: false,
        retries: 0,
        error: false,
      },
      contextUuid: uuid(),
    };
  },

  computed: {
    ...mapGetters({
      getBestsHistory: "bests/getBestsHistory",
      getAvailableBests: "bests/getAvailableBests",
      getUserBests: "bests/getUserBests",
      getWebsocketIsConnected: "config/getWebsocketIsConnected",
      // marketdata: "marketdata/getMarketdata",
    }),
    headers() {
      switch (this.selected_tab) {
        case "Disponíveis":
          return [
            {
              label: "Ativo",
              col_span: 2,
            },
            {
              label: "Tendência",
              col_span: 1,
            },
            {
              label: "Multiplicador",
              col_span: 2,
            },
            {
              label: "Objetivo",
              col_span: 3,
            },
            {
              label: "Best",
              col_span: 4,
            },
          ];
        case "Em andamento":
          return [
            {
              label: "Ativo",
              col_span: 2,
            },
            {
              label: "Entrada",
              col_span: 2,
            },
            {
              label: "Best",
              col_span: 2,
            },
            {
              label: "Multiplicador",
              col_span: 2,
            },
            {
              label: "Resultado",
              col_span: 2,
            },
            {
              label: "Parar",
              col_span: 2,
              hidden: true,
            },
          ];
        case "Histórico":
          return [
            {
              label: "Best",
              col_span: 2,
            },
            {
              label: "Status",
              col_span: 2,
            },
            {
              label: "Direção",
              col_span: 1,
            },
            {
              label: "Entrada",
              col_span: 2,
            },
            {
              label: "Saída",
              col_span: 2,
            },
            {
              label: "Multipli\u00ADca\u00ADdor",
              col_span: 1,
            },
            {
              label: "Resultado",
              col_span: 2,
            },
          ];
      }
    },
    tableToShow() {
      switch (this.selected_tab) {
        case "Disponíveis":
          return "bestAvailableTableRowComponent";
        case "Em andamento":
          return "bestRunningTableRowComponent";
        case "Histórico":
          return "bestHistoryTableRowComponent";
      }
    },
    bodyData() {
      switch (this.selected_tab) {
        case "Disponíveis":
          return this.getAvailableBests;
        case "Em andamento":
          return this.getUserBests;
        case "Histórico":
          return this.getBestsHistory;
        default:
          return [];
      }
    },
    activeTopics() {
      return this.getAvailableBests.map((item) => `MD.${item.best_symbol}.L1`);
    },
    activeBests() {
      return this.getUserBests.filter((item) => item.bo_status == "ACTIVE");
    },
  },

  methods: {
    ...mapActions({ updateBest: "bests/updateBest" }),
    async wait(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },
    async consume() {
      // await this.wait(5000);
      if (this.connectionStatus.retries > 5) {
        this.connectionStatus.error = true;
        this.connectionStatus.awaitingConnection = false;
        console.error("[BESTS] Erro ao conectar ao websocket.");
        return;
      }
      if (!this.getWebsocketIsConnected) {
        if (this.isSharedWorker) {
          this.worker.sendMessage({ type: "CHECK_CONNECTION" });
        }
        this.connectionStatus.retries++;
        this.connectionStatus.awaitingConnection = true;
        setTimeout(() => {
          this.consume();
        }, 2000);
        return;
      }
      this.connectionStatus.awaitingConnection = false;
      this.connectionStatus.error = false;
      this.worker.sendMessage({
        type: "CONSUME",
        data: {
          uuid: this.contextUuid,
          topics: this.activeTopics,
          type: "Bests",
        },
      });
      this.worker.sendMessage({
        type: "LOG",
      });
    },
    onMessage(e) {
      // console.log(e);
      const selected_bests = this.activeBests.filter((item) => item.bo_symbol == e.symbol);
      selected_bests.forEach((item) => {
        this.updateBest(item);
      });
    },
    stopConsuming() {
      this.worker.sendMessage({
        type: "STOP_CONSUMING",
        data: this.contextUuid,
      });
    },
  },

  watch: {
    activeTopics: {
      immediate: true,
      handler(val) {
        if (val.length) {
          this.consume();
          // console.log(val);
        }
      },
    },
    // selected_tab: {
    //   handler(val) {
    //     if (val == "Histórico") {
    //       this.stopConsuming();
    //     } else {
    //       this.consume();
    //     }
    //   },
    // },
  },

  // mounted() {
  //   event_bus.$on("MARKET_DATA", (e) => {
  //     this.onMessage(e);
  //   });
  // },

  destroyed() {
    this.stopConsuming();
  },
};
</script>

<style lang="scss" scoped>
.header-cell {
  @apply px-4 py-2 text-sm font-bold;
}
.fade {
  &-move,
  &-enter-active,
  &-leave-active {
    @apply transition-all duration-300 ease-in-out;
  }
  &-leave-active {
    position: absolute;
  }
  &-enter, &-leave-to /* .fade-leave-active below version 2.1.8 */ {
    @apply opacity-0;
  }
}
</style>
